import { useState, useCallback, useEffect, useRef } from 'react';
import { supabase } from '../clients/supabaseClient';
import { fetchPickingDashboard, type PickingDashboardOrder } from '../services/pickingDashboardService';
import { getErrorMessage } from '../utils/errors';

// ── usePickingDashboard ───────────────────────────────────────────────────────

// Manages fetching and realtime refresh of the picking dashboard.
// Also drives the 1-second tick used for live elapsed-time display —
// the tick re-renders existing data without refetching from the DB.
export function usePickingDashboard() {
    const [orders,  setOrders]  = useState<PickingDashboardOrder[]>([]);
    const [loading, setLoading] = useState(true);
    const [error,   setError]   = useState<string | null>(null);
    const [now,     setNow]     = useState(Date.now());

    const refreshDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const load = useCallback(async () => {
        try {
            const data = await fetchPickingDashboard();
            setOrders(data);
            setError(null);
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to load picking dashboard'));
        } finally {
            setLoading(false);
        }
    }, []);

    // Initial fetch
    useEffect(() => {
        void load();
    }, [load]);

    // Tick every second purely for the live elapsed-time display —
    // does not refetch, just re-renders existing data with updated durations.
    useEffect(() => {
        const tick = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(tick);
    }, []);

    // Realtime: any change to order_status_history (new assignment) or
    // picking_sessions (started/completed/cancelled) triggers a refresh.
    // Debounced so a burst of changes doesn't spam refetches.
    useEffect(() => {
        const scheduleRefresh = () => {
            if (refreshDebounceRef.current) clearTimeout(refreshDebounceRef.current);
            refreshDebounceRef.current = setTimeout(() => void load(), 500);
        };

        const channel = supabase
            .channel('picking_dashboard_live')
            .on('postgres_changes', { event: '*', schema: 'public', table: 'order_status_history' }, scheduleRefresh)
            .on('postgres_changes', { event: '*', schema: 'public', table: 'picking_sessions' }, scheduleRefresh)
            .on('postgres_changes', { event: '*', schema: 'public', table: 'picking_session_items' }, scheduleRefresh)
            .subscribe();

        return () => {
            if (refreshDebounceRef.current) clearTimeout(refreshDebounceRef.current);
            void supabase.removeChannel(channel);
        };
    }, [load]);

    return { orders, loading, error, now };
}